<template>
  <div class="video-form-generation-wrapper">
    <div class="video-form-generation">
      <!-- Main Form Container -->
      <div class="form-container">
        <!-- Media Assets Section -->
        <div class="form-section">
          <div class="section-title">
            <h3 class="title-text">
              <span class="input-label-required">*</span>
              Media assets you'd like in your video
            </h3>
            <p class="description-text">
              These videos that showcase your product or service.
            </p>
          </div>

          <div class="upload-area">
            <div class="selected-videos-grid">
              <div
                v-for="video in selectedVideos"
                :key="video.id"
                class="video-preview-card"
                @mouseleave="stopVideo(video.id)"
              >
                <div class="video-preview">
                  <video
                    :ref="`video-${video.id}`"
                    :src="video.videoUrl"
                    class="preview-video"
                    preload="metadata"
                    muted
                    @ended="onVideoEnded(video.id)"
                  >
                    Your browser does not support the video tag.
                  </video>

                  <!-- Simple play button - only show when not playing -->
                  <div
                    v-if="!playingVideos[video.id]"
                    class="play-button"
                    @click="playVideo(video.id)"
                  >
                    <feather-icon
                      icon="PlayIcon"
                      size="20"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="ai-analyze-section">
            <b-button
              variant="primary"
              size="sm"
              class="ai-analyze-btn"
              :disabled="aiAnalyzeLoading"
              @click="performAIAnalysis"
            >
              <feather-icon
                v-if="aiAnalyzeLoading"
                icon="LoaderIcon"
                size="14"
                class="spinning"
              />
              <feather-icon
                v-else
                icon="ZapIcon"
                size="14"
              />
              {{ aiAnalyzeLoading ? 'Analyzing...' : 'AI Analyze' }}
            </b-button>
          </div>
        </div>

        <!-- Product Name Section -->
        <div class="form-section">
          <div class="input-group">
            <label class="input-label">
              <span class="input-label-required">*</span>
              Product name
            </label>
            <div class="input-container">
              <input
                v-model="formData.productName"
                type="text"
                placeholder="Enter product name"
                class="form-input"
                :disabled="aiAnalyzeLoading"
                maxlength="500"
              >
            </div>
            <div class="character-count">
              {{ formData.productName.length }}/500
            </div>
          </div>
        </div>

        <!-- Product Description Section -->
        <div class="form-section">
          <div class="input-group">
            <label class="input-label">
              <span class="input-label-required">*</span>
              Product description
            </label>
            <div class="input-container textarea-container">
              <textarea
                v-model="formData.productDescription"
                placeholder="Describe your product and service"
                class="form-textarea"
                rows="3"
                :disabled="aiAnalyzeLoading"
                maxlength="500"
              />
            </div>
            <div class="character-count">
              {{ formData.productDescription.length }}/500
            </div>
          </div>
        </div>

        <!-- Selling Points Section -->
        <div class="form-section">
          <div class="input-group">
            <label class="input-label">
              <span class="input-label-required">*</span>
              Selling points
            </label>
            <p class="input-hint">
              Share the unique selling points of your product or service. Recommended length for each selling point: no more than 10 words.
              <br><br>
              Example: relaxed fit unisex t-shirt made from a mid-weight 100% soft cotton jersey.
            </p>
            <div class="selling-points-container">
              <div class="input-container selling-points-input">
                <div class="tags-input-wrapper">
                  <div
                    v-for="(point, index) in formData.sellingPoints"
                    :key="index"
                    class="selling-point-tag"
                  >
                    <span
                      class="tag-text"
                      @mouseenter="showTooltip($event, point)"
                      @mouseleave="hideTooltip"
                    >{{ point }}</span>
                    <button
                      class="remove-tag-btn"
                      :disabled="aiAnalyzeLoading"
                      @click="removeSellingPoint(index)"
                    >
                      <feather-icon
                        icon="XIcon"
                        size="14"
                      />
                    </button>
                  </div>
                  <input
                    v-model="sellingPointsInput"
                    type="text"
                    :placeholder="formData.sellingPoints.length > 0 ? 'Add another selling point...' : 'Example: New Customer Discount. 50% Off Summer Special.'"
                    class="form-input tags-input"
                    maxlength="500"
                    :disabled="aiAnalyzeLoading"
                    @keyup.enter="addSellingPoint"
                    @blur="addSellingPoint"
                    @keydown.backspace="handleBackspaceOnEmpty"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Two Column Section -->
        <div class="two-column-section">
          <!-- Brand Section -->
          <div class="form-section">
            <div class="input-group">
              <label class="input-label">Brand (Optional)</label>
              <div class="input-container">
                <input
                  v-model="formData.brand"
                  type="text"
                  placeholder="Enter Brand name"
                  class="form-input"
                  maxlength="100"
                >
              </div>
              <div class="character-count">
                {{ formData.brand.length }}/100
              </div>
            </div>
          </div>

          <!-- Price Section -->
          <div class="form-section">
            <div class="input-group">
              <label class="input-label">Price (Optional)</label>
              <div class="price-input-group">
                <div class="input-container price-input">
                  <input
                    v-model="formData.price"
                    type="number"
                    placeholder="0.00"
                    class="form-input"
                    min="0"
                    step="0.01"
                  >
                </div>
                <div class="currency-selector">
                  <select
                    v-model="formData.currency"
                    class="currency-select"
                  >
                    <option
                      v-for="currency in currencies"
                      :key="currency.value"
                      :value="currency.value"
                    >
                      {{ currency.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Language Section -->
        <div class="form-section">
          <div class="input-group">
            <label class="input-label">Language</label>
            <div class="language-selector">
              <select
                v-model="formData.language"
                class="language-select"
              >
                <option
                  v-for="lang in languageOptions"
                  :key="lang.value"
                  :value="lang.value"
                >
                  {{ lang.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="form-section">
          <div class="input-group">
            <label class="input-label">Digital Avatar (Optional)</label>
            <p class="input-hint">
              The chosen avatar will be used to generate the Avatar Product video.
            </p>
            <!-- Avatar Section -->
            <VideoFormAvatar
              :selected-avatar="formData.avatar"
              @avatar-selected="onAvatarSelected"
              @avatar-removed="onAvatarRemoved"
            />
          </div>
        </div>
        <!-- Number of Videos Section -->
        <div class="form-section">
          <div class="input-group">
            <label class="input-label">Number of videos</label>
            <div class="video-count-selector">
              <button
                v-for="count in [1, 2, 3, 4, 5]"
                :key="count"
                :class="['count-button', { active: formData.videoCount === count }]"
                @click="formData.videoCount = count"
              >
                {{ count }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Custom Tooltip -->
      <div
        v-if="tooltip && tooltip.visible"
        class="custom-tooltip"
        :style="{
          left: tooltip.x + 'px',
          top: tooltip.y + 'px',
          transform: 'translateX(-50%)',
        }"
      >
        {{ tooltip.text }}
      </div>

      <!-- Action Buttons -->
      <div class="form-generate-actions">
        <b-button
          variant="outline-secondary"
          class="back-button"
          @click="goBackToSelection"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="16"
          />
          Back
        </b-button>
        <button
          class="generate-video-btn"
          :disabled="!canGenerateForm || isGenerating"
          @click="generateForm"
        >
          <feather-icon
            v-if="!isGenerating"
            icon="ZapIcon"
            size="16"
          />
          <feather-icon
            v-else
            icon="LoaderIcon"
            size="16"
            class="spinning"
          />
          {{ isGenerating ? 'Generating...' : 'Generate video' }}
        </button>
      </div>
    </div>

    <!-- Library Modal -->
    <LibraryModal
      :show="showLibraryModal"
      :current-selected-videos="selectedVideos"
      @videos-selected="handleLibraryVideosSelected"
      @selection-changed="handleLibrarySelectionChanged"
      @close="handleLibraryModalClose"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { BButton } from 'bootstrap-vue'
import { CURRENCIES } from '@/constants/currencies'
import { languageOptions } from '@/constants/languages'
import VideoFormAvatar from './VideoFormAvatar.vue'
import LibraryModal from './LibraryModal.vue'

export default {
  name: 'VideoFormGeneration',
  components: {
    BButton,
    VideoFormAvatar,
    LibraryModal,
  },
  data() {
    return {
      currencies: CURRENCIES,
      languageOptions,
      formData: {
        productName: '',
        productDescription: '',
        sellingPoints: [],
        brand: '',
        price: '',
        currency: 'USD',
        language: 'en',
        videoCount: 1,
        productType: 'PRODUCT',
        videoUrls: this.getInitialVideoUrls(),
        avatar: null,
      },
      sellingPointsInput: '',
      isGenerating: false,
      tooltip: {
        visible: false,
        text: '',
        x: 0,
        y: 0,
      },
      librarySelectionBackup: [],
      libraryConfirmed: false,
      showLibraryModal: false,
      playingVideos: {}, // Track which videos are currently playing
    }
  },
  computed: {
    ...mapGetters('videoGeneration', [
      'selectedVideos',
      'selectedVideosCount',
      'aiAnalyzeLoading',
      'aiAnalyzeError',
      'aiAnalyzeResult',
    ]),
    canGenerateForm() {
      return this.formData.productName.trim().length > 0
             && this.formData.productDescription.trim().length > 0
             && this.formData.sellingPoints.length > 0
    },
    videoUrls() {
      return this.selectedVideos?.map(video => video.videoUrl).filter(Boolean) || []
    },
  },
  watch: {
    selectedVideos: {
      handler() {
        this.updateVideoUrlsFromStore()
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    sanitizeSellingPoint(raw) {
      if (!raw) return ''
      // Remove line breaks, collapse spaces, trim
      const cleaned = String(raw).replace(/\s+/g, ' ').trim()
      // Enforce max length to keep UI intact
      const MAX_LEN = 500
      return cleaned.length > MAX_LEN ? cleaned.slice(0, MAX_LEN) : cleaned
    },
    ...mapActions('videoGeneration', [
      'clearSelectedVideos',
      'addSelectedVideo',
      'setSelectedVideos',
      'generateFormVideo',
      'analyzeImageWithAI',
    ]),

    getInitialVideoUrls() {
      const selectedVideos = this.$store.getters['videoGeneration/selectedVideos']
      return selectedVideos?.map(video => video.videoUrl).filter(Boolean) || []
    },

    goBackToSelection() {
      // Clear all selected videos from Vuex store
      this.clearSelectedVideos()

      // Clear form data video URLs
      this.formData.videoUrls = []

      // Emit the event to go back
      this.$emit('go-back-to-selection')
    },

    openLibraryModal() {
      this.librarySelectionBackup = [...(this.selectedVideos || [])]
      this.libraryConfirmed = false
      this.showLibraryModal = true
    },

    handleLibraryVideosSelected(selectedVideos) {
      // Preserve locally uploaded videos and merge with library selection
      const currentVideos = [...(this.selectedVideos || [])]
      const uploadedVideos = currentVideos.filter(video => video.isUploaded)

      // Combine uploaded videos with library selection
      const mergedVideos = [...uploadedVideos, ...selectedVideos]
      this.setSelectedVideos(mergedVideos)
      this.updateVideoUrlsFromStore()
      this.showLibraryModal = false
      this.libraryConfirmed = true
    },

    handleLibrarySelectionChanged(selectedVideos) {
      // Handle real-time selection changes in the modal
      // This updates the selection state as user checks/unchecks videos
      // Only store library videos in backup, uploaded videos are preserved separately
      this.librarySelectionBackup = [...selectedVideos]
    },

    handleLibraryModalClose() {
      // Apply the final selection state when modal is closed
      if (this.librarySelectionBackup.length >= 0) { // Allow empty selection
        // Preserve locally uploaded videos and merge with library selection
        const currentVideos = [...(this.selectedVideos || [])]
        const uploadedVideos = currentVideos.filter(video => video.isUploaded)

        // Combine uploaded videos with library selection
        const mergedVideos = [...uploadedVideos, ...this.librarySelectionBackup]
        this.setSelectedVideos(mergedVideos)
        this.updateVideoUrlsFromStore()
      }

      this.showLibraryModal = false
      this.librarySelectionBackup = []
      this.libraryConfirmed = false
    },

    addSellingPoint() {
      if (this.aiAnalyzeLoading) return
      const input = this.sellingPointsInput.trim()
      if (!input) return

      const point = this.sanitizeSellingPoint(input)
      if (!point || point.length < 2) {
        this.$toast.warning('Selling point is too short')
        return
      }

      if (!this.formData.sellingPoints.includes(point)) {
        this.formData.sellingPoints.push(point)
      }
      this.sellingPointsInput = ''
    },

    handleBackspaceOnEmpty(event) {
      if (!this.sellingPointsInput.length && this.formData.sellingPoints.length > 0) {
        event.preventDefault()
        this.formData.sellingPoints.pop()
      }
    },

    removeSellingPoint(index) {
      if (this.aiAnalyzeLoading) return
      this.formData.sellingPoints.splice(index, 1)
    },

    showTooltip(event, text) {
      // Only show tooltip if text is actually truncated
      const element = event.target
      if (element.scrollWidth > element.clientWidth) {
        const rect = element.getBoundingClientRect()
        this.tooltip = {
          visible: true,
          text,
          x: rect.left + rect.width / 2,
          y: rect.top - 8,
        }
      }
    },

    hideTooltip() {
      this.tooltip.visible = false
    },

    onAvatarSelected(avatar) {
      this.formData.avatar = {
        // eslint-disable-next-line no-underscore-dangle
        _id: avatar._id,
        avatarId: avatar.avatarId,
        avatarName: avatar.avatarName,
        avatarThumbnail: avatar.avatarThumbnail,
        avatarPreviewUrl: avatar.avatarPreviewUrl,
      }
    },

    onAvatarRemoved() {
      this.formData.avatar = null
      this.$toast.success('Avatar removed successfully')
    },

    // AI Analyze methods
    async performAIAnalysis() {
      try {
        if (!this.selectedVideos.length) {
          this.$toast.warning('Please select at least one video first')
          return
        }

        const { imageUrl } = this.selectedVideos[0]
        if (!imageUrl) {
          this.$toast.warning('Selected video does not have an image URL for analysis')
          return
        }

        const result = await this.analyzeImageWithAI({ imageUrl })

        if (result.success) {
          this.$toast.success('AI analysis completed successfully!')
          this.mapAIResultToFormData(result.data)
        } else {
          this.$toast.error(result.error?.message || 'Failed to analyze image')
        }
      } catch (error) {
        this.$toast.error('Failed to analyze image with AI')
      }
    },

    mapAIResultToFormData(aiResult) {
      if (aiResult.title) this.formData.productName = aiResult.title
      if (aiResult.description) this.formData.productDescription = aiResult.description

      if (aiResult.sellingPoints?.length) {
        this.formData.sellingPoints = aiResult.sellingPoints
          .map(p => this.sanitizeSellingPoint(p))
          .filter(p => p?.length >= 2)
          .slice(0, 10)
      }
    },

    getAdvertiserId() {
      const fromRoute = this.$route?.query?.advertiserId
      if (fromRoute) return fromRoute
      try {
        return new URL(window.location.href).searchParams.get('advertiserId') || ''
      } catch {
        return ''
      }
    },

    async generateForm() {
      if (!this.canGenerateForm) {
        this.$toast.warning('Please fill in all required fields')
        return
      }

      this.isGenerating = true

      try {
        const payload = {
          productType: this.formData.productType,
          productVideoInfo: {
            videoGenerationCount: this.formData.videoCount,
            targetLanguage: this.formData.language,
            productInfo: {
              productName: this.formData.productName,
              title: this.formData.productName,
              description: this.formData.productDescription,
              sellingPoints: this.formData.sellingPoints,
              currency: this.formData.currency,
            },
          },
          selectedVideos: this.selectedVideos,
          videoUrls: this.formData.videoUrls,
          advertiserId: this.getAdvertiserId(),
          avatarInfo: {
            avatarId: this.formData?.avatar?.avatarId ?? '',
          },
        }

        const result = await this.generateFormVideo(payload)

        if (result.success) {
          this.$emit('generate-form', result.data)
        } else {
          this.$toast.error(result.error?.data?.message || 'Failed to generate form')
        }
      } finally {
        this.isGenerating = false
      }
    },

    updateVideoUrlsFromStore() {
      this.formData.videoUrls = this.getInitialVideoUrls()
    },

    // Simple video controls
    playVideo(videoId) {
      const ref = this.$refs[`video-${videoId}`]
      const videoElement = Array.isArray(ref) ? ref[0] : ref
      if (videoElement) {
        videoElement.play()
        this.$set(this.playingVideos, videoId, true)
      }
    },

    stopVideo(videoId) {
      const ref = this.$refs[`video-${videoId}`]
      const videoElement = Array.isArray(ref) ? ref[0] : ref
      if (videoElement) {
        videoElement.pause()
        this.$set(this.playingVideos, videoId, false)
      }
    },

    onVideoEnded(videoId) {
      this.$set(this.playingVideos, videoId, false)
    },
  },
}
</script>

<style lang="scss" scoped>
.video-form-generation {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-width: 1156px;
  margin: 0 auto;

  .form-container {
    background: #ffffff;
    border-radius: 32px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 1156px;
    width: 100%;
    margin: 0 auto;
  }

  .input-label-required {
    color: #dc3545;
  }

  .two-column-section {
    display: flex;
    flex-direction: row;
    justify-content: stretch;
    align-items: stretch;
    gap: 24px;
    align-self: stretch;

    .form-section {
      flex: 1;
    }
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .section-title {
      display: flex;
      flex-direction: column;
      gap: 16px;

      .title-text {
        font-weight: 400;
        font-size: 16px;
        line-height: 1.5em;
        color: #000000;
        margin: 0;
      }

      .description-text {
        font-weight: 400;
        font-size: 14px;
        line-height: 1.43em;
        color: rgba(0, 0, 0, 0.7);
        margin: 0;
      }
    }

    .upload-area {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 16px;
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: 16px;
      align-self: stretch;

      .selected-videos-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 12px;
        width: 100%;

        .video-preview-card {
          background: #f8f9fa;
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 8px;
          padding: 0;
          transition: all 0.3s ease;
          overflow: hidden;
          aspect-ratio: 9 / 16; // Makes the card 9:16 ratio (portrait)

          &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .video-preview {
            position: relative;
            width: 100%;
            height: 100%;
            aspect-ratio: 1; // Ensures square container

            .preview-video {
              width: 100%;
              height: 100%;
              object-fit: cover;
              border-radius: 8px;
              pointer-events: none; // Disable all video interactions
            }

            // Simple play button
            .play-button {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              display: flex;
              align-items: center;
              justify-content: center;
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: rgba(0, 0, 0, 0.7);
              color: white;
              cursor: pointer;
              transition: all 0.2s ease;
              z-index: 2;

              &:hover {
                background: rgba(0, 0, 0, 0.8);
                transform: translate(-50%, -50%) scale(1.1);
              }
            }
          }
        }
      }

    }

    .ai-analyze-section {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 8px;
      align-self: stretch;

      .ai-analyze-btn {
        background: linear-gradient(141deg, rgba(129, 255, 244, 1) 0%, rgba(110, 68, 255, 1) 100%);
        border: none;
        border-radius: 100px;
        height: 32px;
        font-weight: 400;
        font-size: 14px;
        line-height: 1;
        color: #ffffff;
        display: flex;
        align-items: center;
        gap: 6px;

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }
      }
    }

    .input-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
      box-shadow: none;

      .input-label {
        font-weight: 400;
        font-size: 16px;
        line-height: 1.5em;
        color: #000000;
        margin: 0;
      }

      .input-hint {
        font-weight: 400;
        font-size: 14px;
        line-height: 1.43em;
        color: rgba(0, 0, 0, 0.7);
        margin: 0;
      }

      .input-container {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 8px 16px;
        background: #ffffff;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 8px;
        min-height: 40px;
        align-self: stretch;

        &.textarea-container {
          align-items: flex-start;
          padding: 12px 16px;
          min-height: 80px;
        }

        .input-icon {
          color: rgba(0, 0, 0, 0.5);
          margin-top: 2px;
          flex-shrink: 0;
        }

        .form-input {
          flex: 1;
          border: none;
          outline: none;
          font-weight: 400;
          font-size: 16px;
          line-height: 1.5em;
          color: #000000;

          &::placeholder {
            color: rgba(0, 0, 0, 0.5);
          }

          &:disabled {
            background: #f6f7f9;
            color: rgba(0, 0, 0, 0.45);
            cursor: not-allowed;
          }

          &[type="number"] {
            appearance: textfield;
            -moz-appearance: textfield;

            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
          }
        }

        .form-textarea {
          flex: 1;
          border: none;
          outline: none;
          font-weight: 400;
          font-size: 16px;
          line-height: 1.5em;
          color: #000000;
          resize: vertical;
          min-height: 200px;
          align-self: stretch;
          padding: 0;
          background: transparent;

          &::placeholder {
            color: rgba(0, 0, 0, 0.5);
          }

          &:disabled {
            background: #f6f7f9;
            color: rgba(0, 0, 0, 0.45);
            cursor: not-allowed;
          }
        }
      }

      .character-count {
        font-weight: 400;
        font-size: 14px;
        line-height: 1.43em;
        color: rgba(0, 0, 0, 0.7);
        text-align: right;
      }

      .selling-points-container {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .selling-points-input {
          margin-bottom: 0;
          padding: 8px 16px;
          min-height: 40px;
          align-items: flex-start;
          height: auto;
          overflow: hidden;
          flex-wrap: wrap;

          .tags-input-wrapper {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 6px;
            flex: 1;
            min-height: 24px;
            width: 100%;

            .selling-point-tag {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 4px 10px;
              background: linear-gradient(141deg, rgba(129, 255, 244, 0.1) 0%, rgba(110, 68, 255, 0.1) 100%);
              border: 1px solid rgba(129, 255, 244, 0.3);
              border-radius: 16px;
              transition: all 0.3s ease;
              flex-shrink: 0;
              max-width: 100%;

              &:hover {
                background: linear-gradient(141deg, rgba(129, 255, 244, 0.15) 0%, rgba(110, 68, 255, 0.15) 100%);
                border-color: rgba(129, 255, 244, 0.5);
                transform: translateY(-1px);
              }

              .tag-text {
                font-weight: 400;
                font-size: 16px;
                color: #000000;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 280px;
              }

              .remove-tag-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 25px;
                height: 25px;
                background: rgba(220, 53, 69, 0.1);
                border: none;
                border-radius: 50%;
                color: #dc3545;
                cursor: pointer;
                transition: all 0.3s ease;

                &:hover {
                  background: rgba(220, 53, 69, 0.2);
                  transform: scale(1.1);
                }

                &:disabled {
                  opacity: 0.6;
                  cursor: not-allowed;
                  transform: none;
                }
              }
            }

            .tags-input {
              flex: 1;
              min-width: 120px;
              border: none;
              outline: none;
              font-weight: 400;
              font-size: 16px;
              line-height: 1.5em;
              color: #000000;
              background: transparent;
              padding: 0;
              min-height: 28px;
              margin-top: 2px;

              &::placeholder {
                color: rgba(0, 0, 0, 0.5);
              }

              &:disabled {
                background: #f6f7f9;
                color: rgba(0, 0, 0, 0.45);
                cursor: not-allowed;
              }
            }
          }
        }
      }
    }

    .price-input-group {
      display: flex;
      align-items: stretch;
      gap: -1px;
      align-self: stretch;

      .price-input {
        flex: 1;
        border-radius: 8px 0 0 8px;
      }

      .currency-selector {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 16px;
        background: #ffffff;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 0 8px 8px 0;
        width: 114px;

        .currency-select {
          border: none;
          outline: none;
          font-weight: 400;
          font-size: 16px;
          line-height: 1.5em;
          color: rgba(0, 0, 0, 0.5);
          background: transparent;
          width: 100%;
        }

        .dropdown-icon {
          color: #292D32;
        }
      }
    }

    .language-selector {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 16px;
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: 8px;
      height: 40px;
      align-self: stretch;

      .language-select {
        border: none;
        outline: none;
        font-weight: 400;
        font-size: 16px;
        line-height: 1.5em;
        color: #000000;
        background: transparent;
        width: 100%;
      }

      .dropdown-icon {
        color: #292D32;
      }
    }

    .video-count-selector {
      display: flex;
      align-items: center;
      gap: 6px;
      align-self: stretch;

      .count-button {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        padding: 8px;
        width: 45px;
        height: auto;
        background: #ffffff;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 8px;
        font-weight: 400;
        font-size: 14px;
        line-height: 1.43em;
        color: #000000;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: linear-gradient(141deg, rgba(129, 255, 244, 1) 0%, rgba(110, 68, 255, 1) 100%);
          border: none;
          color: #ffffff;
        }

        &.active {
          background: linear-gradient(141deg, rgba(129, 255, 244, 1) 0%, rgba(110, 68, 255, 1) 100%);
          border: none;
          color: #ffffff;
        }
      }
    }
  }

  .form-generate-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    align-self: stretch;

    .generate-video-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 12px 16px;
      height: 40px;
      border: none;
      border-radius: 100px;
      background: linear-gradient(141deg, rgba(129, 255, 244, 1) 0%, rgba(110, 68, 255, 1) 100%);
      color: #ffffff;
      font-weight: 400;
      font-size: 14px;
      line-height: 1.57em;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(110, 68, 255, 0.3);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
      }

      .spinning {
        animation: spin 1s linear infinite;
      }
    }
  }
}

.back-button {
  padding: 10px 16px !important;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.custom-tooltip {
  position: fixed;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.4;
  max-width: 300px;
  word-wrap: break-word;
  white-space: normal;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: none;
}

</style>
